import { ReplaySubject } from 'rxjs';
import { initAutoScaling } from '../../../../graph/auto-scale';
import { DataSet } from '../../../../graph/dataset';
import { DATA_TYPE, type DataTypeValue } from '../../../../graph/enums';
import { QueryCanceledError, Scales } from '../../../../graph/graph-helpers';
import type { Graph } from '../../../../graph/graph';
import { LegendData } from '../../../../graph/legend-helpers';
import { distanceConverter } from '../../../../utils/formatting';
import type { Profile } from '../../../../profile';
import type {
    GraphRuntimeState,
    ElementGraphRuntimeSettings,
    GraphTimeWindow,
    MeasuringPointModel,
    RenderRuntimeGraphSettings,
} from './types';
import { mergeElementGraphRuntimeSettings } from './graph/runtimeSettings';

interface GraphRuntimeManager {
    getGraphTimeWindow(): GraphTimeWindow;
    getGraphRuntimeState(
        measuringPoint: MeasuringPointModel,
        elementGraphSettings?: ElementGraphRuntimeSettings,
        bindingGraphSettings?: RenderRuntimeGraphSettings
    ): GraphRuntimeState;
    registerRenderedGraph(
        measuringPoint: MeasuringPointModel,
        graph: Graph,
        elementGraphSettings?: ElementGraphRuntimeSettings
    ): () => void;
    finalizeGraphAutoScaling(): void;
    getRenderedGraphs(): Graph[];
}

function getGraphCacheKey(
    measuringPoint: MeasuringPointModel,
    graphSettings: RenderRuntimeGraphSettings
): string {
    const cacheableGraphSettings = {
        viewStartTs: graphSettings.viewStartTs,
        viewEndTs: graphSettings.viewEndTs,
        preferredGraphType: graphSettings.preferredGraphType,
        frequencyGraphScaleType: graphSettings.frequencyGraphScaleType,
        valueScale: graphSettings.valueScale,
        valueScaleEquation: graphSettings.valueScaleEquation,
        frequencyScale: graphSettings.frequencyScale,
        hiddenLines: graphSettings.hiddenLines,
        dataType: graphSettings.dataType,
    };

    return JSON.stringify({
        measuringPointId: measuringPoint.id,
        ...cacheableGraphSettings,
        alarmsUrl: graphSettings.getAlarmsUrl(measuringPoint.id),
    });
}

const FDOM_DATA_TYPE: DataTypeValue = DATA_TYPE.FDOM;

function autoScaleTargetsFixedFrequencyScale(graphSettings: RenderRuntimeGraphSettings): boolean {
    return graphSettings.dataType === FDOM_DATA_TYPE && graphSettings.frequencyScale !== null;
}

export function createGraphRuntimeManager(
    graphSettings: RenderRuntimeGraphSettings | undefined,
    profile: Profile
): GraphRuntimeManager {
    const graphRuntimeCache = new Map<string, GraphRuntimeState>();
    const graphRuntimeSettingsByCacheKey = new Map<string, RenderRuntimeGraphSettings>();
    const graphInstancesByCacheKey = new Map<string, Set<Graph>>();

    function requireGraphSettings(): RenderRuntimeGraphSettings {
        if (graphSettings) {
            return graphSettings;
        }

        throw new Error(
            'Missing graph runtime settings. Pass graph settings to createRenderRuntime(...) before rendering graph elements.'
        );
    }

    function resolveGraphSettings(
        elementGraphSettings?: ElementGraphRuntimeSettings
    ): RenderRuntimeGraphSettings {
        return mergeElementGraphRuntimeSettings(requireGraphSettings(), elementGraphSettings);
    }

    function getGraphTimeWindow(): GraphTimeWindow {
        const currentGraphSettings = requireGraphSettings();

        return {
            viewStartTs: currentGraphSettings.viewStartTs,
            viewEndTs: currentGraphSettings.viewEndTs,
        };
    }

    function getGraphRuntimeState(
        measuringPoint: MeasuringPointModel,
        elementGraphSettings?: ElementGraphRuntimeSettings,
        bindingGraphSettings?: RenderRuntimeGraphSettings
    ): GraphRuntimeState {
        const currentGraphSettings = bindingGraphSettings
            ? mergeElementGraphRuntimeSettings(bindingGraphSettings, elementGraphSettings)
            : resolveGraphSettings(elementGraphSettings);
        const cacheKey = getGraphCacheKey(measuringPoint, currentGraphSettings);
        const cachedState = graphRuntimeCache.get(cacheKey);
        if (cachedState) {
            return cachedState;
        }

        const dataSet = new DataSet(
            measuringPoint.id,
            (measuringPointId) => currentGraphSettings.getAlarmsUrl(measuringPointId),
            {
                name: measuringPoint.name,
                deviceName: measuringPoint.deviceName,
                swarmType: measuringPoint.swarmType,
                permission: measuringPoint.permission,
                enableFetchTraces: false,
                dataType: currentGraphSettings.dataType,
                reportMode: true,
            }
        );
        // `fetchEvents` is wrapped specifically because its failure leaves `config$` stuck
        // at `undefined`, which hangs table readiness forever. Other fetchers
        // (fetchSamples, fetchVperPeriods, etc.) are not wrapped here; a failure there
        // still causes `dataSet.ready` to hang because `DataSet.request` does not surface
        // per-fetcher rejections through a shared error channel. The full fix requires
        // #9772 (RxJS-driven DataSet request handling), where each data source owns its
        // own error state. `ReplaySubject` so late subscribers still see a failure that
        // already happened.
        const dataSetRequestError$ = new ReplaySubject<unknown>(1);
        const originalFetchEvents = dataSet.fetchEvents.bind(dataSet);
        dataSet.fetchEvents = (async (...args: Parameters<typeof originalFetchEvents>) => {
            try {
                return await originalFetchEvents(...args);
            } catch (error) {
                // A cancellation means a newer request superseded this one, not a failure.
                if (!(error instanceof QueryCanceledError)) {
                    dataSetRequestError$.next(error);
                }

                throw error;
            }
        }) as typeof dataSet.fetchEvents;

        dataSet.setVisibleMinMax(
            Math.floor(currentGraphSettings.viewStartTs),
            Math.ceil(currentGraphSettings.viewEndTs)
        );
        dataSet.checkDataUpdateNeeded(true);

        const scales = new Scales(profile);
        const legendData = new LegendData();

        if (currentGraphSettings.valueScale) {
            scales.set(
                currentGraphSettings.dataType,
                distanceConverter(currentGraphSettings.valueScale, profile.distanceUnit)
            );
        }

        if (currentGraphSettings.frequencyScale) {
            scales.set('fdom', currentGraphSettings.frequencyScale);
        }

        currentGraphSettings.hiddenLines.forEach((hiddenLine) => {
            legendData.setHidden(hiddenLine, true);
        });
        legendData.removeType('pm10-information');

        const graphRuntimeState: GraphRuntimeState = {
            dataSet,
            dataSetRequestError$,
            scales,
            legendData,
            preferredGraphType: currentGraphSettings.preferredGraphType,
            frequencyGraphScaleType: currentGraphSettings.frequencyGraphScaleType,
        };

        graphRuntimeCache.set(cacheKey, graphRuntimeState);
        graphRuntimeSettingsByCacheKey.set(cacheKey, currentGraphSettings);

        return graphRuntimeState;
    }

    function registerRenderedGraph(
        measuringPoint: MeasuringPointModel,
        graph: Graph,
        elementGraphSettings?: ElementGraphRuntimeSettings
    ): () => void {
        const currentGraphSettings = resolveGraphSettings(elementGraphSettings);
        const cacheKey = getGraphCacheKey(measuringPoint, currentGraphSettings);
        graphRuntimeSettingsByCacheKey.set(cacheKey, currentGraphSettings);
        const existingGraphs = graphInstancesByCacheKey.get(cacheKey);
        const graphsForKey = existingGraphs ?? new Set<Graph>();
        if (!existingGraphs) {
            graphInstancesByCacheKey.set(cacheKey, graphsForKey);
        }
        graphsForKey.add(graph);

        return () => {
            const currentGraphs = graphInstancesByCacheKey.get(cacheKey);
            if (!currentGraphs) {
                return;
            }
            currentGraphs.delete(graph);
            if (currentGraphs.size === 0) {
                graphInstancesByCacheKey.delete(cacheKey);
            }
        };
    }

    function finalizeGraphAutoScaling(): void {
        graphInstancesByCacheKey.forEach((graphs, cacheKey) => {
            if (graphs.size === 0) {
                return;
            }

            const graphRuntimeState = graphRuntimeCache.get(cacheKey);
            if (!graphRuntimeState) {
                throw new Error(
                    `Missing graph runtime state while finalizing auto scaling for key "${cacheKey}".`
                );
            }
            const currentGraphSettings = graphRuntimeSettingsByCacheKey.get(cacheKey);
            if (!currentGraphSettings) {
                throw new Error(
                    `Missing graph runtime settings while finalizing auto scaling for key "${cacheKey}".`
                );
            }

            // FDOM is the frequency scale key, so value auto-scaling would overwrite
            // an explicitly fixed frequency scale.
            if (autoScaleTargetsFixedFrequencyScale(currentGraphSettings)) {
                return;
            }

            initAutoScaling(
                currentGraphSettings.valueScale,
                graphRuntimeState.dataSet,
                graphRuntimeState.scales,
                Array.from(graphs),
                currentGraphSettings.valueScaleEquation,
                currentGraphSettings.dataType,
                profile
            );
        });
    }

    function getRenderedGraphs(): Graph[] {
        return Array.from(graphInstancesByCacheKey.values()).flatMap((graphsForKey) =>
            Array.from(graphsForKey)
        );
    }

    return {
        getGraphTimeWindow,
        getGraphRuntimeState,
        registerRenderedGraph,
        finalizeGraphAutoScaling,
        getRenderedGraphs,
    };
}
